<script setup lang="ts">
import { computed, ref } from "vue";
import ConceptLinks from "~/features/python/ch2/exercise/components/ConceptLinks.vue";
import ExplanationBlock from "~/features/python/ch2/exercise/components/ExplanationBlock.vue";
import QuestionAttempt from "~/features/python/ch2/exercise/components/QuestionAttempt.vue";
import QuestionHero from "~/features/python/ch2/exercise/components/QuestionHero.vue";
import QuestionSolution from "~/features/python/ch2/exercise/components/QuestionSolution.vue";
import SolutionBlock from "~/features/python/ch2/exercise/components/SolutionBlock.vue";
import type { Question } from "~/features/python/ch2/exercise/types/exercise.types";

const props = defineProps<{
  question: Question;
  position: number;
  total: number;
}>();

const submittedAnswer = ref<number | string | null>(null);
const attemptKey = ref(0);
const progressPercent = computed(() => Math.round((props.position / props.total) * 100));

const isCorrect = computed(() => {
  if (submittedAnswer.value === null) {
    return null;
  }

  return String(submittedAnswer.value).trim() === String(props.question.correct).trim();
});

function handleSubmit(answer: number | string) {
  submittedAnswer.value = answer;
}

function retryQuestion() {
  submittedAnswer.value = null;
  attemptKey.value += 1;
}
</script>

<template>
  <article :id="`q-${question.id}`" class="scroll-mt-24 space-y-6 rounded-[30px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 md:p-6">
    <div class="rounded-2xl border border-slate-200 bg-white/95 p-3 backdrop-blur dark:border-slate-800 dark:bg-slate-950/95">
      <div class="grid grid-cols-[auto_1fr_auto] items-center gap-3">
        <p class="text-sm font-medium text-slate-700 dark:text-slate-200">Sawal {{ question.number }}</p>
        <p class="text-center text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {{ position }} of {{ total }}
        </p>
        <div class="h-1.5 w-16 rounded-full bg-slate-200 dark:bg-slate-800">
          <div class="h-1.5 rounded-full bg-sky-500" :style="{ width: `${progressPercent}%` }" />
        </div>
      </div>
    </div>

    <QuestionHero :question="question" />

    <section class="grid gap-6 xl:grid-cols-[1.05fr_0.95fr]">
      <div class="space-y-6">
        <QuestionAttempt :key="`${question.id}-${attemptKey}`" :question="question" @submitted="handleSubmit" />
        <ExplanationBlock
          :question="question"
          :has-submitted="submittedAnswer !== null"
          :is-correct="isCorrect"
        />
        <QuestionSolution
          :question="question"
          :has-submitted="submittedAnswer !== null"
          :is-correct="isCorrect"
        />
      </div>

      <div class="space-y-6">
        <SolutionBlock :question="question" />
        <ConceptLinks :question="question" />
      </div>
    </section>

    <section class="rounded-3xl border border-slate-200 bg-slate-50 p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950">
      <div class="flex flex-wrap items-center gap-3">
        <a href="#program-list" class="rounded-full bg-slate-100 px-4 py-2 text-sm font-medium text-slate-800 dark:bg-slate-800 dark:text-slate-100">
          Upar list par jao
        </a>
        <button
          v-if="submittedAnswer !== null && isCorrect === false"
          type="button"
          class="rounded-full bg-rose-600 px-4 py-2 text-sm font-medium text-white"
          @click="retryQuestion"
        >
          Retry Question
        </button>
      </div>
    </section>
  </article>
</template>
